import {useMemo, forwardRef, ReactElement} from 'react';
import './list-item-link.scss';
import {LinkProps, NavLink, NavLinkProps} from 'react-router-dom';
import {SxProps} from '@mui/system';
import {useTheme, Theme, ListItem, ListItemIcon, ListItemText, Tooltip} from '@mui/material';
import {Pallete} from '../../theme/bluebird';

interface ListItemLinkProps {
  icon?: ReactElement;
  endIcon?: ReactElement;
  primary: string;
  to: NavLinkProps['to'];
  expanded?: boolean;
  teamSideMenuDropdown?: boolean;
  teamFirstChild?: boolean;
  teamMiddleChild?: boolean;
  teamProfileDropdown?: boolean;
  exact?: boolean;
  sx?: SxProps<Theme>;
  showTooltip?: boolean;
  target?: string;
  isActive?: NavLinkProps['isActive'];
}

/**
 * List item with a link.
 */
export const ListItemLink = ({
  icon,
  endIcon,
  primary,
  to,
  expanded,
  teamSideMenuDropdown,
  teamFirstChild,
  teamMiddleChild,
  teamProfileDropdown,
  exact,
  sx,
  showTooltip = false,
  target,
  isActive,
}: ListItemLinkProps) => {
  // Get theme
  const theme = useTheme();

  const renderLink = useMemo(
    () =>
      forwardRef<HTMLAnchorElement, Omit<LinkProps, 'to'>>(function Link(itemProps, ref) {
        // console.log(itemProps.className, ref, to);
        return (
          <NavLink
            to={to}
            ref={ref}
            {...itemProps}
            role={undefined}
            activeStyle={{
              backgroundColor: teamProfileDropdown ? '#D4E7FF' : theme.palette.primary.main,
              color: teamProfileDropdown ? '#464646' : theme.palette.background.paper,
            }}
            exact={exact ?? false}
            isActive={isActive}
            className={`${itemProps.className} ${
              teamProfileDropdown && 'list-sub-item-link'
            } list-item-link`}
            target={target}
          />
        );
      }),
    [to, theme, teamProfileDropdown, target, exact, isActive]
  );

  const renderListItem = () => (
    <ListItem
      button
      component={renderLink}
      className={icon ? 'has-icon' : ''}
      sx={{
        boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
        borderRadius: '5px',
        backgroundColor: theme.palette.background.paper,
        marginBottom: teamFirstChild || teamProfileDropdown || teamMiddleChild ? '0' : '8px',
        marginTop: teamMiddleChild ? '8px' : '',
        height: '48px',
        px: '8px',
        ...sx,
      }}
    >
      {icon ? (
        teamSideMenuDropdown ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ListItemIcon
              sx={{
                color: Pallete.blue.base,
                mr: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: '24px',
                },
              }}
            >
              {icon}
            </ListItemIcon>
            {!expanded && endIcon && (
              <ListItemIcon
                sx={{
                  color: Pallete.blue.base,
                  '& .MuiSvgIcon-root': {
                    fontSize: '14px',
                  },
                }}
              >
                {endIcon}
              </ListItemIcon>
            )}
            <></>
          </div>
        ) : (
          <ListItemIcon
            sx={{
              color: Pallete.blue.base,
              mr: '6px',
              '& .MuiSvgIcon-root': {
                fontSize: '24px',
              },
            }}
          >
            {icon}
          </ListItemIcon>
        )
      ) : null}
      <ListItemText
        className="list-item-link-text"
        sx={{
          whiteSpace: 'nowrap',
          color: Pallete.blue.base,
          '& .MuiTypography-root': {
            fontStyle: 'normal',
            fontWeight: '600',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.1px',
            textOverflow: 'ellipsis',
            wordWrap: 'break-word',
            overflow: 'hidden',
          },
        }}
        primary={primary}
      />
      {endIcon && expanded ? (
        <ListItemIcon
          sx={{
            color: Pallete.blue.base,
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
            },
          }}
        >
          {endIcon}
        </ListItemIcon>
      ) : null}
    </ListItem>
  );

  return showTooltip ? (
    <Tooltip placement="right" title={primary}>
      {renderListItem()}
    </Tooltip>
  ) : (
    renderListItem()
  );
};
